import {useEffect, useState, useRef, useMemo } from 'react';
import {Heading, HEADING_TYPES, DatePicker, ChoiceChips} from 'cdk-radial'
import { CommonDropdown } from "../../common/CommonSelector";
import { useTranslation } from "react-i18next";
import { useDashboardData } from '../useDashboard';
import { APP_ENV } from '../../../constants';
import userProfileStore from '../../core/UserProfileStore';
import useUserSettingStore from '../UserSettingStore';
import styled from 'styled-components';
import {accountingPOUrl} from '../DashboardConfig';
import {PO_DASHBOARD} from '../usePODashboard';
import moment from 'moment';

export const getUserDetailConfig = (loginId:string) =>{
  const config = [];
  config.push( 
        {
          id: 'group',
          apiConfig: {
            method:'GET',
            headers:{
              'x-department-name':'',
              'x-department-type':'',
              'remote_user':loginId,
            },
            path:"/purchase-order-bff/user-details-with-account-type/A",
            endpoint: accountingPOUrl[APP_ENV as unknown as keyof typeof accountingPOUrl],
          },
        });
  return config;
}


export const StyledFieldRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
  @media (max-width: 1100px) {
    display: grid;
    max-width: 70%;
  }
`;

export const StyledDatePickerWrapper = styled.div<{ maxWidth?: string }>`
  max-width: ${(props) => props.maxWidth || '250px'};
  width: 100%;
  position: relative;
  .DayPicker {
    position: absolute;
    left: 0;
    top: 100%;
    margin-top: 8px;
    z-index: 1000;
  }
`;

export const DatePresetChipGroup = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  min-width: 300px;
  
  @media (max-width: 768px) {
    min-width: 100%;
  }
`;



const getFilterSetting = ( 
  filter:any,
  userDetailInfo:any,
) => {
  const poGroupInfoList =  userDetailInfo?.poGroupInfoList?.[0];
  let defaultDepartment = poGroupInfoList?.deptName ;
  let defaultStoreId = userDetailInfo?.extendedAccountInfo?.find((acct:any) => acct.accounts.includes(defaultDepartment))?.storeId;
  let defaultGroupCode = poGroupInfoList?.groupCode ;

  if(filter){
    let filterDept = userDetailInfo?.poGroupInfoList?.find((group:any) => group.groupCode === filter.group.groupCode);
    let departmentName = filterDept?.deptName;
    let departmentStoreId = userDetailInfo?.extendedAccountInfo?.find((acct:any) => acct.accounts.includes(departmentName))?.storeId;
      return {
        storeId: departmentStoreId,
        department: departmentName,
        group: filter.group.groupCode,
        startDate: filter.startDate,
        endDate: filter.endDate,
        dateRangePreset: filter.dateRangePreset
      };
  }

  return {
    storeId: defaultStoreId,
    department:defaultDepartment,
    group: defaultGroupCode,
    startDate: undefined,
    endDate: undefined,
    dateRangePreset: 30 // Default to Last Month
  }
}


export const usePODashboardFilter = ({userSetting, enabled = true}:any) => {

    const { unifyUserProfile, } = userProfileStore();
    const userAppliedFilter = userSetting.dashboard?.find((d: any) => d.id === PO_DASHBOARD.DASHBOARD_ID)?.filter;

    const [userDetailConfig, setUserDetailConfig] = useState<any>(null);
    const [filterSetting, setFilterSetting] = useState() as any;

    const userDetailData = useDashboardData({
        config: userDetailConfig,
        enabled: enabled && !!userDetailConfig,
    });

    useEffect(() => {
      if (!enabled) {
        setUserDetailConfig(null);
        setFilterSetting(undefined);
        return;
      }
      setUserDetailConfig(getUserDetailConfig(unifyUserProfile.loginId));
    }, [unifyUserProfile?.loginId, enabled]);

    useEffect(() => {
        if (userDetailData?.rawData?.[0]) {
            const userDetailInfo = userDetailData?.rawData?.[0];
            setFilterSetting(getFilterSetting(userAppliedFilter, userDetailInfo ));
        }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSetting, userDetailData?.rawData?.[0]?.poGroupInfoList]);

  const isLoading = enabled &&
    !userDetailData?.error &&
    (userDetailData.isLoading || !filterSetting)

  return {
    isLoading,
    error: userDetailData?.error ,
    filterSetting: filterSetting
  };

}

export const POFilter = ({ onChange, onValidationError, widget }: any) => {

    const { userSetting } = useUserSettingStore();
    const {isLoading , filterSetting, } = usePODashboardFilter({userSetting});
    const [userDetailConfig, setUserDetailConfig] = useState<any>(null);
    const { unifyUserProfile, } = userProfileStore();
    const {t} = useTranslation();

    const userDetailData = useDashboardData({
        config: userDetailConfig,
        enabled: !!userDetailConfig,
    });

    const [groupOptions,setGroupOptions] = useState() as any;
    const [selectGroup, setSelectGroup] = useState() as any;
    
    // Date range filter state
    const [startDate, setStartDate] = useState<any>(null);
    const [endDate, setEndDate] = useState<any>(null);
    const [datePreset, setDatePreset] = useState<string>('custom');

    // Block future dates from tomorrow onwards
    const blockedFutureDates = useMemo(() => [
        {
            startDate: moment().add(1, 'day'),
            endDate: moment('2100-12-31')
        }
    ], []);
    const tempStartDateRef = useRef<any>(null);
    const tempEndDateRef = useRef<any>(null);
    const isDateModeInitialized = useRef(false);
    
    // Disable save button only while loading data
    useEffect(() => {
        onValidationError(isLoading);
    }, [isLoading, onValidationError]);

    // Validate dates: require either both start/end dates OR a preset (not custom)
    useEffect(() => {
        // If dates are provided, check if either is missing or in the future
        if ((startDate && !endDate) || (!startDate && endDate)) {
            onValidationError(true);
        }
        // Check if either date is in the future
        else if (startDate && moment(startDate).isAfter(moment(), 'day')) {
            onValidationError(true);
        }
        else if (endDate && moment(endDate).isAfter(moment(), 'day')) {
            onValidationError(true);
        }
        // If no dates and no preset selected, disable save
        else if (!startDate && !endDate && datePreset === 'custom') {
            onValidationError(true);
        }
        else if (!isLoading) {
            // Only enable if not loading and valid dates/preset are selected
            onValidationError(false);
        }
    }, [startDate, endDate, datePreset, isLoading, onValidationError]);
    
    // Initialize
    useEffect(() => {
      setUserDetailConfig(getUserDetailConfig(unifyUserProfile.loginId));
    }, [unifyUserProfile.loginId]);

   useEffect(()=>{ 
    const rawData = userDetailData?.rawData?.[0];
    const allGroup = rawData?.poGroupInfoList;
    if( filterSetting?.group){
      const options = allGroup?.map((group:any) => {
        return {
          value: group?.groupCode,
          label: group?.groupCode,
          obj: group
        }
      });
      setGroupOptions(options);
      const go = options?.find((o:any) => o.label === filterSetting.group);
      setSelectGroup(go);
      
      // Initialize date mode (only once)
      if (!isDateModeInitialized.current) {
        if (filterSetting?.startDate && filterSetting?.endDate) {
          const savedStartDate = moment(filterSetting.startDate);
          const savedEndDate = moment(filterSetting.endDate);
          setStartDate(savedStartDate);
          setEndDate(savedEndDate);
          tempStartDateRef.current = savedStartDate;
          tempEndDateRef.current = savedEndDate;
          setDatePreset('custom');
        } else if (filterSetting?.dateRangePreset) {
          setStartDate(null);
          setEndDate(null);
          if (filterSetting.dateRangePreset === 6) {
            setDatePreset('7days');
          } else if (filterSetting.dateRangePreset === 13) {
            setDatePreset('14days');
          } else if (filterSetting.dateRangePreset === 30) {
            setDatePreset('lastmonth');
          }
        } else {
          setStartDate(null);
          setEndDate(null);
          setDatePreset('lastmonth');
        }
        isDateModeInitialized.current = true;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[isLoading,filterSetting,userDetailData?.rawData?.[0]?.poGroupInfoList]);

  // Update date preset when filterSetting changes (for modal reopening)
  useEffect(() => {
    if (!filterSetting) return;
    
    const hasStartDate = filterSetting.startDate && filterSetting.startDate !== '';
    const hasEndDate = filterSetting.endDate && filterSetting.endDate !== '';
    
    if (hasStartDate && hasEndDate) {
      const savedStartDate = moment(filterSetting.startDate);
      const savedEndDate = moment(filterSetting.endDate);
      setStartDate(savedStartDate);
      setEndDate(savedEndDate);
      tempStartDateRef.current = savedStartDate;
      tempEndDateRef.current = savedEndDate;
      setDatePreset('custom');
    } else {
      const dateRangePreset = Number(filterSetting.dateRangePreset);
      
      if (!isNaN(dateRangePreset) && dateRangePreset > 0) {
        setStartDate(null);
        setEndDate(null);
        
        if (dateRangePreset === 6) {
          setDatePreset('7days');
        } else if (dateRangePreset === 13) {
          setDatePreset('14days');
        } else if (dateRangePreset === 30) {
          setDatePreset('lastmonth');
        } else {
          setDatePreset('lastmonth');
        }
      } else {
        setStartDate(null);
        setEndDate(null);
        setDatePreset('lastmonth');
      }
    }
  }, [filterSetting]);

  const handleSelectGroup = (group: any) => {
        setSelectGroup(group);
        
        // Determine date values
        let startDateValue = null;
        let endDateValue = null;
        let dateRangePresetValue = 0;
        
        if (startDate && endDate) {
          startDateValue = startDate.format('YYYY-MM-DD');
          endDateValue = endDate.format('YYYY-MM-DD');
        } else if (datePreset !== 'custom') {
          if (datePreset === '7days') dateRangePresetValue = 6;
          else if (datePreset === '14days') dateRangePresetValue = 13;
          else if (datePreset === 'lastmonth') dateRangePresetValue = 30;
        }
        
        onChange({
            group: group?.obj,
            startDate: startDateValue,
            endDate: endDateValue,
            dateRangePreset: dateRangePresetValue
        });
    };
    
    const handleStartDateChange = (date: any) => {
        setStartDate(date);
        tempStartDateRef.current = date;
        setDatePreset('custom'); // Reset preset when date is selected
        
        onChange({
            group: selectGroup?.obj,
            startDate: date ? date.format('YYYY-MM-DD') : null,
            endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
            dateRangePreset: 0
        });
    };
    
    const handleEndDateChange = (date: any) => {
        setEndDate(date);
        tempEndDateRef.current = date;
        setDatePreset('custom'); // Reset preset when date is selected
        
        onChange({
            group: selectGroup?.obj,
            startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
            endDate: date ? date.format('YYYY-MM-DD') : null,
            dateRangePreset: 0
        });
    };
    
    const handleDatePreset = (preset: string) => {
        setDatePreset(preset);
        setStartDate(null); // Reset date pickers when preset is selected
        setEndDate(null);
        tempStartDateRef.current = null; // Clear temp refs
        tempEndDateRef.current = null;
        
        let daysAgo;
        switch(preset) {
            case '7days':
                daysAgo = 6;
                break;
            case '14days':
                daysAgo = 13;
                break;
            case 'lastmonth':
                daysAgo = 30;
                break;
            default:
                return;
        }
        
        onChange({
            group: selectGroup?.obj,
            startDate: null,
            endDate: null,
            dateRangePreset: daysAgo
        });
    };

  return (
        <div >

                <Heading level={3} type={HEADING_TYPES.SUBTITLE_3} >
                    Date Settings
                </Heading>
              
                <DatePresetChipGroup>
                    <ChoiceChips
                        dataTestId="date-preset-chips"
                        onClick={(value: string) => handleDatePreset(value)}
                        id="date-preset-chips"
                        name="datePreset"
                        options={[
                            { label: t('dashboard.datePresets.last7Days') as string, value: '7days' },
                            { label: t('dashboard.datePresets.last14Days') as string, value: '14days' },
                            { label: t('dashboard.datePresets.lastMonth') as string, value: 'lastmonth' }
                        ]}
                        value={datePreset === 'custom' ? '' : datePreset}
                        isDisabled={isLoading}
                    />
                </DatePresetChipGroup>
                
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '10px' }}>
                    <StyledDatePickerWrapper maxWidth="250px">
                        <DatePicker
                            dataTestId="po-filter-start-date-picker"
                            date={startDate}
                            dateFormat="MM/DD/YYYY"
                            dateInputProps={{
                                enableCustomValidation: false,
                                errorMessage: '',
                                hasError: false,
                                isReadOnly: false,
                                isRequired: false,
                                isDisabled: isLoading,
                                size: 'standard',
                                onError: () => {},
                            }}
                            allowDaysInPast={true}
                            blockedDateRanges={blockedFutureDates}
                            id="po-filter-start-date-picker"
                            label="Start Date"
                            maximumYear={2100}
                            minimumYear={1900}
                            name="po-filter-start-date-picker"
                            numberOfMonths={1}
                            onDateChange={handleStartDateChange}
                            shortcutChipsPosition="none"
                        />
                    </StyledDatePickerWrapper>
                    
                    <StyledDatePickerWrapper maxWidth="250px">
                        <DatePicker
                            dataTestId="po-filter-end-date-picker"
                            date={endDate}
                            dateFormat="MM/DD/YYYY"
                            dateInputProps={{
                                enableCustomValidation: false,
                                errorMessage: '',
                                hasError: false,
                                isReadOnly: false,
                                isRequired: false,
                                isDisabled: isLoading,
                                size: 'standard',
                                onError: () => {},
                            }}
                            allowDaysInPast={true}
                            blockedDateRanges={blockedFutureDates}
                            id="po-filter-end-date-picker"
                            label="End Date"
                            maximumYear={2100}
                            minimumYear={1900}
                            name="po-filter-end-date-picker"
                            numberOfMonths={1}
                            onDateChange={handleEndDateChange}
                            shortcutChipsPosition="none"
                        />
                    </StyledDatePickerWrapper>
                </div>

            <Heading level={3} type={HEADING_TYPES.SUBTITLE_3} >Filter Settings (Purchase Order)</Heading>
            <div style={{marginTop:'5px'}}>
                <StyledFieldRow>
                    <div style={{ flex: 1 }}>
                    <CommonDropdown
                          options={groupOptions || []}
                          name="group"
                          label="Select Group"
                          placeholder=""
                          selectedOption={selectGroup}
                          onSelect={handleSelectGroup} />
                    </div>
                </StyledFieldRow>
            </div>
        </div>
  );
};
